/**
 * Configure the OpenAPI client with authentication and error handling
 */
import { OpenAPI } from './index';
import { tokenStorage } from '../lib/auth/token-storage';

let refreshPromise: Promise<void> | null = null;

/**
 * Refresh tokens if needed
 * Ensures only one refresh happens at a time
 */
async function ensureValidToken(): Promise<string | null> {
  // Check if token needs refresh
  if (tokenStorage.shouldRefresh() && tokenStorage.getRefreshToken()) {
    // Avoid multiple simultaneous refresh requests
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          const { useAuthStore } = await import('../lib/auth/auth-store');
          await useAuthStore.getState().refreshTokens();
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Let the 401 interceptor handle logout
        } finally {
          refreshPromise = null;
        }
      })();
    }
    
    await refreshPromise;
  }

  return tokenStorage.getAccessToken();
}

/**
 * Configure API client with base URL and authentication
 */
export function configureApi() {
  // Set base URL from environment variable (baked in at build time)
  OpenAPI.BASE = import.meta.env.VITE_API_URL || '';
  OpenAPI.WITH_CREDENTIALS = false; // We use bearer tokens, not cookies
  
  console.log('API configured with base URL:', OpenAPI.BASE);
  
  // Configure token retrieval with automatic refresh
  OpenAPI.TOKEN = async () => {
    const token = await ensureValidToken();
    return token || '';
  };

  // Request interceptor - persist tokens before navigation/refresh + apply fetch timeout
  OpenAPI.interceptors.request.use(async (request) => {
    // Persist tokens to sessionStorage before any page navigation
    // This allows recovery after page refresh
    if (typeof window !== 'undefined') {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation?.type === 'reload') {
        tokenStorage.persistToSession();
      }
    }

    // Combine existing cancellation signal with a 15 s timeout so requests
    // never hang indefinitely (e.g. when the backend is slow to respond).
    const timeoutSignal = AbortSignal.timeout(15_000);
    request.signal = request.signal
      ? AbortSignal.any([request.signal as AbortSignal, timeoutSignal])
      : timeoutSignal;

    return request;
  });

  // Response interceptor - handle 401 errors
  OpenAPI.interceptors.response.use(async (response) => {
    if (response.status === 401) {
      // Unauthorized - clear auth and redirect to login
      const { useAuthStore } = await import('../lib/auth/auth-store');
      useAuthStore.getState().logout();
      
      // Redirect to login if not already there
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return response;
  });
}
